import { Amount } from '@emberswap/emberswap-sdk2/dist/entities'
import { Type } from '@emberswap/emberswap-sdk2/dist/types'
import dynamic from 'next/dynamic'
import { FC, ReactNode, useState } from 'react'
import { Address } from 'viem'

import { classNames } from '../../functions'
import { ApprovalState, useERC20ApproveCallback } from '../../hooks/useERC20ApproveCallback'
import Button, { Props } from '../Button'
import { IconButton } from '../Icons'
import { ChevronDownIcon } from '../Icons'
import { Select, SelectContent, SelectItem, SelectPrimitive } from '../Select'
import { NotificationData } from '../Toast'

export interface ApproveERC20Props extends Props {
  id: string
  amount: Amount<Type> | undefined
  contract: Address | undefined
  enabled?: boolean
  className?: string
  children: ReactNode
  onSuccess(data: NotificationData): void
}

export const Component: FC<ApproveERC20Props> = ({
  id,
  amount,
  contract,
  children,
  className = '',
  fullWidth = true,
  size = 'md',
  enabled = true,
  ...props
}) => {
  const [max, setMax] = useState(false)

  const [approvalState, onApprove] = useERC20ApproveCallback(
    Boolean(!!amount && !!contract),
    amount,
    contract,
    max
  )
  if (approvalState === ApprovalState.APPROVED || !enabled) {
    return <>{children}</>
  }

  const loading = [ApprovalState.UNKNOWN, ApprovalState.LOADING, ApprovalState.PENDING].includes(
    approvalState
  )

  return (
    <div className={classNames(className, 'justify-center items-center text-center w-full')}>
      <Button
        color="gradient"
        // key={index}
        loading={loading}
        testdata-id={id}
        variant="outlined"
        size={size}
        className={classNames(className, 'flex ')}
        fullWidth={fullWidth}
        onClick={onApprove}
      >
        <div className="grid w-full grid-flow-col grid-cols-1 mx-auto text-center">
          Approve {amount?.currency.symbol} {max ? 'Permanently' : ''}
          <div className="col-span-2 my-auto place-content-center flex flex-col gap-1 w-6 h-6 disabled:pointer-events-none disabled:saturate-[0] cursor-pointer">
            <Select value={`${max}`} onValueChange={(val) => setMax(val === 'true')}>
              <SelectPrimitive.Trigger>
                <IconButton className="p-1 rounded-full bg-slate-400/50 hover:bg-slate-400/80">
                  <ChevronDownIcon
                    width={20}
                    height={20}
                    className="fill-slate-400/80 hover:fill-slate-400"
                    x
                  />
                </IconButton>
              </SelectPrimitive.Trigger>
              <SelectContent className=" w-80">
                <SelectItem value="false">
                  <div className="flex flex-col pointer-events-auto">
                    <span className="font-semibold">Approve one-time only</span>
                    <span className="text-sm">
                      You&apos;ll give your approval to spend {amount?.toSignificant(6)}{' '}
                      {amount?.currency?.symbol} on your behalf
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="true">
                  <div className="flex flex-col pointer-events-auto">
                    <span className="font-semibold">Approve unlimited amount</span>
                    <span className="text-sm">
                      You won&apos;t need to approve again next time you want to spend{' '}
                      {amount?.currency?.symbol}.
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Button>
    </div>
  )
}

export const ApproveERC20 = dynamic(() => Promise.resolve(Component), {
  ssr: false,
})
